/**
 * Survey (lawnmower / boustrophedon) path generation for coverage shapes
 * (issue #347, part of #24).
 *
 * A coverage shape only *defines* a region. To fly it, the area is swept with
 * parallel lines a fixed distance apart; each line is clipped to the shape
 * boundary and the clipped spans are walked in alternating directions, so the
 * result is one continuous ordered path that covers the whole area.
 *
 * Everything here is pure geometry — no Leaflet, no Redux, no DOM — so it is
 * exercised directly in a unit test.
 */
import { PlannerShape } from '../types/missionPlanner';
import { LatLngTuple, OffsetM, offsetToLatLng, shapeOffsets } from './geometry';

/** How the sweep is laid out over the shape. */
export interface SurveyOptions {
  /** Distance between adjacent sweep lines, in metres. Must be positive. */
  lineSpacingM: number;
  /** Direction the sweep lines run, in degrees clockwise from north. */
  sweepAngleDeg: number;
}

/** Camera geometry a line spacing can be derived from, instead of a raw metre value. */
export interface CameraSpacing {
  /** Survey altitude above ground, in metres. */
  altitudeM: number;
  /** Across-track camera field of view, in degrees (0 < fov < 180). */
  fovDeg: number;
  /** Desired overlap between adjacent swaths, in percent (0 ≤ overlap < 100). */
  overlapPercent: number;
}

/**
 * Cap on the waypoints a single generated path may contain. A survey that
 * needs more than this is almost certainly a mis-typed spacing, and a route
 * that size is a memory problem on the Pi Zero — refuse it loudly rather than
 * quietly truncating the coverage.
 */
export const MAX_SURVEY_WAYPOINTS = 500;

/**
 * Clipped spans shorter than this contribute no useful coverage (they are the
 * slivers cut off a corner or the tip of an ellipse) and the neighbouring
 * swath's overlap already covers them, so they are dropped rather than costing
 * two waypoints and a pair of turns each.
 */
const MIN_SPAN_M = 1;

/** A point in the sweep frame: `along` runs down a line, `across` between lines. */
interface SweepPoint {
  along: number;
  across: number;
}

/**
 * Rotate a local east/north offset into the sweep frame for `bearingDeg`. The
 * transform is a reflection, hence its own inverse — `fromSweepFrame` below is
 * the same arithmetic with the axes swapped back.
 */
const toSweepFrame = (
  { east, north }: OffsetM,
  bearingDeg: number
): SweepPoint => {
  const φ = (bearingDeg * Math.PI) / 180;
  return {
    along: east * Math.sin(φ) + north * Math.cos(φ),
    across: east * Math.cos(φ) - north * Math.sin(φ),
  };
};

/** Rotate a sweep-frame point back into a local east/north offset. */
const fromSweepFrame = (
  { along, across }: SweepPoint,
  bearingDeg: number
): OffsetM => {
  const φ = (bearingDeg * Math.PI) / 180;
  return {
    east: along * Math.sin(φ) + across * Math.cos(φ),
    north: along * Math.cos(φ) - across * Math.sin(φ),
  };
};

/**
 * Where the sweep line at `across` crosses the outline, as sorted `along`
 * values. Edges are tested half-open (one endpoint inside, one outside) so a
 * vertex lying exactly on the line is counted once, not twice, and a vertex the
 * line merely touches is not counted at all. Crossings therefore always pair up
 * into inside spans, which is what makes concave outlines clip correctly.
 */
const crossings = (outline: SweepPoint[], across: number): number[] => {
  const hits: number[] = [];
  for (let i = 0; i < outline.length; i += 1) {
    const p = outline[i];
    const q = outline[(i + 1) % outline.length];
    if ((p.across <= across) === (q.across <= across)) continue;
    const t = (across - p.across) / (q.across - p.across);
    hits.push(p.along + t * (q.along - p.along));
  }
  return hits.sort((a, b) => a - b);
};

/**
 * Line spacing (m) that yields the requested overlap for a camera with `fovDeg`
 * across-track field of view flown at `altitudeM`. The ground swath is
 * 2·h·tan(fov/2); spacing is that swath minus the overlap.
 */
export const lineSpacingFromCamera = ({
  altitudeM,
  fovDeg,
  overlapPercent,
}: CameraSpacing): number => {
  const swathM = 2 * altitudeM * Math.tan((fovDeg * Math.PI) / 360);
  return swathM * (1 - overlapPercent / 100);
};

/**
 * Sweep direction giving the fewest turns: along the shape's longest axis. For
 * every kind that is the shape's own rotation, except an ellipse drawn with the
 * larger radius on the minor axis, whose long axis sits 90° away.
 */
export const defaultSweepAngleDeg = (shape: PlannerShape): number =>
  shape.kind === 'ellipse' && shape.radiusMinorM > shape.radiusM
    ? shape.rotationDeg + 90
    : shape.rotationDeg;

/**
 * Ordered [lat, lng] waypoints of a boustrophedon survey path covering `shape`.
 *
 * Sweep lines are spread symmetrically about the shape's centre so that every
 * line's half-spacing footprint stays inside the area's extent — the whole
 * shape is covered, with no wasted line beyond its edge. Consecutive lines run
 * in opposite directions, so the path connects end-to-end with no fly-back.
 *
 * Throws (loudly, rather than returning a partial route) on a non-positive
 * spacing or when the requested sweep would exceed `MAX_SURVEY_WAYPOINTS`.
 */
export const surveyPath = (
  shape: PlannerShape,
  { lineSpacingM, sweepAngleDeg }: SurveyOptions
): LatLngTuple[] => {
  if (!Number.isFinite(lineSpacingM) || lineSpacingM <= 0) {
    throw new Error(
      `Survey line spacing must be a positive number of metres (got ${lineSpacingM}).`
    );
  }
  if (!Number.isFinite(sweepAngleDeg)) {
    throw new Error(`Survey sweep angle must be a number (got ${sweepAngleDeg}).`);
  }

  const outline = shapeOffsets(shape).map((offset) =>
    toSweepFrame(offset, sweepAngleDeg)
  );
  const acrossValues = outline.map((p) => p.across);
  const minAcross = Math.min(...acrossValues);
  const maxAcross = Math.max(...acrossValues);

  // One line per swath needed to span the shape, centred on it so the leftover
  // (span − covered) is split evenly between the two edges.
  const lineCount = Math.max(
    1,
    Math.ceil((maxAcross - minAcross) / lineSpacingM)
  );
  if (lineCount * 2 > MAX_SURVEY_WAYPOINTS) {
    throw new Error(
      `Survey would need ${lineCount} sweep lines (over the ${MAX_SURVEY_WAYPOINTS}-waypoint limit) — increase the line spacing or draw a smaller area.`
    );
  }
  const firstAcross =
    (minAcross + maxAcross) / 2 - ((lineCount - 1) * lineSpacingM) / 2;

  const path: LatLngTuple[] = [];
  for (let line = 0; line < lineCount; line += 1) {
    const across = firstAcross + line * lineSpacingM;
    const hits = crossings(outline, across);

    const spans: Array<[number, number]> = [];
    for (let i = 0; i + 1 < hits.length; i += 2) {
      if (hits[i + 1] - hits[i] >= MIN_SPAN_M) spans.push([hits[i], hits[i + 1]]);
    }

    // Boustrophedon: every other line is flown back the way we came.
    const ordered =
      line % 2 === 1
        ? spans.reverse().map(([a, b]): [number, number] => [b, a])
        : spans;

    for (const [start, end] of ordered) {
      path.push(
        offsetToLatLng(shape.center, fromSweepFrame({ along: start, across }, sweepAngleDeg)),
        offsetToLatLng(shape.center, fromSweepFrame({ along: end, across }, sweepAngleDeg))
      );
    }
  }

  return path;
};
